import { useLingui } from '@lingui/react/macro';
import { isThisYear, isToday, isTomorrow } from 'date-fns';
import { upperFirst } from 'es-toolkit/string';
import { useCallback } from 'react';

import { useCurrentLocale } from '#features/i18n';
import { formatDate } from '../utils/formatDate';
import { useUserTimeSettings } from './useUserTimeSettings';

/**
 * Заголовок дня: «Сегодня», «Завтра», «Понедельник, 11 сент.».
 *
 * Отличается от `useFormatDateRelative` тем, что не показывает время: день здесь сам по себе
 * заголовок, а часы стоят ниже у каждой сессии внутри дня.
 *
 * До этого хука тот же расчёт жил в четырёх местах, и все четыре разошлись: календарь сессий
 * писал «Понедельник, 11 сент.», расписание профиля добавляло год всегда, подзаголовок рядом
 * с ним сокращал день недели до «Пн», а разделитель в переписке собирался вообще другим
 * движком (`Intl`), отчего порядок слов отличался.
 *
 * Год добавляется только когда он не текущий: в переписке за этот год «11 сент. 2026» ничего
 * не уточняет, а в архиве годичной давности — уточняет.
 *
 * День без ведущего нуля: обе заменённые реализации писали `d`, и подзаголовок под этим же
 * заголовком в расписании профиля до сих пор пишет «Чт, 4 сент.». `dd` дало бы «04 сент.»
 * строкой выше «4 сент.» — тот самый разнобой на одном экране, ради которого хук и заводился.
 *
 * Ключи `today`/`tomorrow` строчные и те же, что у `useFormatDateRelative`. Заглавная ставится
 * здесь, `upperFirst`, а не в переводе: иначе на каждое слово заводится по два ключа, и
 * переводчик правит один, а второй остаётся как был.
 */
export const useFormatDayLabel = () => {
  const currentLocale = useCurrentLocale();
  const { timeZone } = useUserTimeSettings();
  const { t } = useLingui();

  return useCallback(
    (dateInput: string | Date, { compact = false }: { compact?: boolean } = {}) => {
      if (isToday(dateInput, { in: timeZone })) return upperFirst(t`today`);
      if (isTomorrow(dateInput, { in: timeZone })) return upperFirst(t`tomorrow`);

      const dayPattern = compact ? 'd MMM' : 'EEEE, d MMM';
      const pattern = isThisYear(dateInput, { in: timeZone }) ? dayPattern : `${dayPattern} yyyy`;

      // `upperFirst`, а не `capitalize`: тот поднимает и месяц, получалось «Понедельник, 04 Сент.».
      return upperFirst(formatDate(dateInput, pattern, { locale: currentLocale, timeZone }));
    },
    [t, currentLocale, timeZone]
  );
};
